import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import Branded from '../components/Branded';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(1.02); }
  to { opacity: 1; transform: scale(1); }
`;

const Section = styled.section`
  background: #f08020;
  padding: 120px 0;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 72px 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const Content = styled.div`
  /* Section sits on the brand orange — the brand name stays white here */
  .brand-decorzo,
  .brand-space {
    color: #fff;
  }

  .tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #fff;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
      content: '';
      width: 24px;
      height: 1px;
      background: #fff;
    }
  }

  h2 {
    font-size: 2.4rem;
    font-weight: 700;
    color: #fff;
    line-height: 1.2;
    margin-bottom: 32px;

    @media (max-width: 768px) {
      font-size: 1.7rem;
    }
  }

  .divider {
    width: 40px;
    height: 2px;
    background: #fff;
    margin-bottom: 32px;
  }

  .desc {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.9;
    white-space: pre-line;
  }
`;

const SliderBlock = styled.div`
  position: relative;
`;

const SliderImages = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;

  @media (max-width: 900px) {
    aspect-ratio: 4 / 3;
  }
`;

const SlideImage = styled.div`
  position: absolute;
  inset: 0;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: opacity 0.8s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    animation: ${({ active }) => (active ? fadeIn : 'none')} 0.8s ease;
  }
`;

const SliderNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
`;

const Counter = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.08em;

  .cur {
    color: #fff;
    font-weight: 600;
    font-size: 20px;
  }

  .total {
    font-size: 13px;
  }
`;

const Arrows = styled.div`
  display: flex;
  gap: 8px;
`;

const ArrowBtn = styled.button`
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
    stroke: rgba(255, 255, 255, 0.85);
    fill: none;
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: stroke 0.3s ease;
  }

  &:hover {
    border-color: #fff;
    background: #fff;
    svg {
      stroke: #f08020;
    }
  }
`;

const ProgressTrack = styled.div`
  margin-top: 16px;
  height: 1px;
  background: rgba(255, 255, 255, 0.18);
  position: relative;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #fff;
  transition: width 0.4s ease;
`;

export default function Celebrate({ tag, title, description, images }) {
  const gallery = images || [];
  const [current, setCurrent] = useState(0);

  const goNext = useCallback(() => {
    setCurrent(prev => (prev + 1) % gallery.length);
  }, [gallery.length]);

  const goPrev = useCallback(() => {
    setCurrent(prev => (prev - 1 + gallery.length) % gallery.length);
  }, [gallery.length]);

  useEffect(() => {
    if (gallery.length <= 1) return;
    const timer = setInterval(goNext, 4000);
    return () => clearInterval(timer);
  }, [goNext, gallery.length, current]);

  if (!tag && !title && !gallery.length) return null;

  const progress =
    gallery.length > 1 ? ((current + 1) / gallery.length) * 100 : 100;

  return (
    <Section>
      <Container>
        <Grid>
          <Content>
            {tag && <div className="tag">{tag}</div>}
            <Branded as="h2" text={title} />
            <div className="divider" />
            <Branded as="p" className="desc" text={description} />
          </Content>

          {gallery.length > 0 && (
            <SliderBlock>
              <SliderImages>
                {gallery.map((img, idx) => (
                  <SlideImage key={idx} active={idx === current}>
                    {img?.asset?.url && (
                      <img
                        src={img.asset.url + '?w=700&auto=format'}
                        alt={`${title || 'Gallery'} ${idx + 1}`}
                      />
                    )}
                  </SlideImage>
                ))}
              </SliderImages>

              {gallery.length > 1 && (
                <>
                  <SliderNav>
                    <Counter>
                      <span className="cur">
                        {String(current + 1).padStart(2, '0')}
                      </span>
                      <span className="total">
                        {' '}
                        / {String(gallery.length).padStart(2, '0')}
                      </span>
                    </Counter>
                    <Arrows>
                      <ArrowBtn onClick={goPrev} aria-label="Previous">
                        <svg viewBox="0 0 24 24">
                          <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                      </ArrowBtn>
                      <ArrowBtn onClick={goNext} aria-label="Next">
                        <svg viewBox="0 0 24 24">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </ArrowBtn>
                    </Arrows>
                  </SliderNav>
                  <ProgressTrack>
                    <ProgressFill style={{ width: `${progress}%` }} />
                  </ProgressTrack>
                </>
              )}
            </SliderBlock>
          )}
        </Grid>
      </Container>
    </Section>
  );
}
